import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { QRCode } from 'react-qrcode-logo';
import * as XLSX from 'xlsx';

import config from '../../../config/config';
import { useBranding } from '../../../contexts/BrandingContext';
import { QRValueTypes, ValuesType } from '../../../interface/qr-generator.interface';
import { useAppDispatch } from '../../../redux/hooks';
import { setPageTitle } from '../../../redux/slices/PageTitleSlice';
import '../scss/qr-code-input-field.scss';
import MyButton from '../../../shared-components/button/Button';
import CustomFileInput from '../../../shared-components/custom-file-input/CustomFileInput';
import CustomInput from '../../../shared-components/custom-input/CustomInput';
import CustomRadioButton from '../../../shared-components/custom-radio-button/CustomRadioButton';
import CustomToggleButton from '../../../shared-components/custom-toggle-button/CustomToggleButton';
import SelectField from '../../../shared-components/select-field/SelectField';
import getFirstSubdomain from '../../../shared-functions/DomainsplitFunction';
import { permissionCheck } from '../../../shared-functions/JWTDocodeFunction';
import { getLocalStorageItem } from '../../../shared-functions/LocalStorage';

import QrConfiguration from './QrConfiguration';
import QrStyle from './QrStyle';

export type { QRValueTypes, ValuesType };

/**
 * QR Code Customization & Generator Component
 * @returns {JSX.Element} QR Code Input Field component layout
 */
function QrCodeInputField(): JSX.Element {
  const { t } = useTranslation('qrCode');
  const dispatch = useAppDispatch();
  const { brandData } = useBranding();

  const localDomain = getLocalStorageItem('client-name');
  const domain = getFirstSubdomain() || localDomain || config.domain || '';
  const rawQrBaseUrl =
    (domain && brandData?.qrCode) ||
    config.qrCodeUrl ||
    config.webChargerUrl ||
    process.env.REACT_APP_API_WEB_CHARGER_URL ||
    window.location.origin;

  let cleanQrBaseUrl = (rawQrBaseUrl || '')
    .trim()
    .replace(/^["']+|["']+$/g, '')
    .replace(/\/+$/, '');

  if (
    cleanQrBaseUrl &&
    !cleanQrBaseUrl.endsWith('/charger') &&
    !cleanQrBaseUrl.includes('/charger?')
  ) {
    cleanQrBaseUrl = `${cleanQrBaseUrl}/charger`;
  }

  /**
   * Format charger serial number into full Web Charging URL for QR code encoding
   * @param {string} deviceSN - Charger serial number
   * @returns {string} Formatted QR URL value
   */
  const getFormattedQrValue = (deviceSN: string): string => {
    if (!deviceSN) return '';
    if (deviceSN.startsWith('http://') || deviceSN.startsWith('https://')) {
      return deviceSN;
    }
    const targetDomain = domain;
    const baseUrl = cleanQrBaseUrl || `${window.location.origin}/charger`;
    return `${baseUrl}?deviceId=${encodeURIComponent(deviceSN)}${targetDomain ? `&domain=${encodeURIComponent(targetDomain)}` : ''}`;
  };

  const [menu, setMenu] = useState<number>(0);
  const [serialNumbersToDownload, setSerialNumbersToDownload] = useState<string[]>([]);
  const [qrExport, setQrExport] = useState<string>('png');
  const [logoUrl, setLogoUrl] = useState<string | undefined>(undefined);
  const qrReference = useRef<(HTMLElement | null)[]>([]);

  const [qrValues, setQrValues] = useState<QRValueTypes>({
    qrstyle: 'squares',
    outerEye: 0,
    innerEye: 0,
    fgColor: '#000000',
    bgColor: '#ffffff',
    outerColor: '#000000',
    innerColor: '#000000',
    font: '',
    fontWeight: '',
    fontColor: '',
    logoWidth: 50,
    logoHeight: 'auto',
    logoTrans: 100,
    logo: null,
    logoName: '',
    qrSize: 150,
    qrMargin: 10,
    qrMethod: 'singleQR',
    deviceSNo: '',
    xlFile: null,
    deviceSerialNumbers: null,
  });

  const [values, setValues] = useState<ValuesType[]>([
    {
      id: 1,
      key: 'qr-code',
      text: t('qrCode'),
      display: true,
      fontFamily: 'Inter',
      fontSize: '16',
      editingText: false,
    },
    {
      id: 2,
      key: 'SN',
      text: 'SN001',
      display: true,
      fontFamily: 'Inter',
      fontSize: '16',
      editingText: false,
    },
    {
      id: 3,
      key: 'T1',
      text: t('sampleText'),
      display: true,
      fontFamily: 'Inter',
      fontSize: '16',
      editingText: false,
    },
    {
      id: 4,
      key: 'T2',
      text: t('brand'),
      display: true,
      fontFamily: 'Inter',
      fontSize: '16',
      editingText: false,
    },
  ]);

  /**
   * Safely handle uploaded logo image file and object URL lifecycle
   */
  useEffect(() => {
    if (qrValues.logo && qrValues.logo.length > 0 && qrValues.logo[0] instanceof File) {
      const url = URL.createObjectURL(qrValues.logo[0]);
      setLogoUrl(url);
      return () => {
        URL.revokeObjectURL(url);
      };
    }
    setLogoUrl(undefined);
    return undefined;
  }, [qrValues.logo]);

  /**
   * Function to handle input changes in row configuration
   * @param {string} name - Getting a name of the input from Drag and Drop Component
   * @param {string | number | boolean} value - Getting value of the input from drag and drop component
   */
  const handleChange = (name: string, value: string | number | boolean) => {
    const splittedName = name.split('/');
    const [key, keyName] = splittedName;
    setValues((previous: ValuesType[]) =>
      previous.map((eachValue: ValuesType) => {
        let updatedValue = eachValue;
        if (updatedValue.key === key) {
          updatedValue = {
            ...updatedValue,
            [keyName]: value,
          };
        }
        return updatedValue;
      }),
    );
  };

  const styleComponents = [
    {
      index: 0,
      component: (
        <QrConfiguration
          handleChange={handleChange}
          values={values}
          setValues={setValues}
          serialNumbersToDownload={serialNumbersToDownload}
        />
      ),
    },
    {
      index: 1,
      component: <QrStyle qrValues={qrValues} setQrValues={setQrValues} />,
    },
    {
      index: 2,
    },
  ];

  /**
   * Handling Selected Components
   * @param {number} index - Updating the index number toggle button to state
   */
  const handelSelection = (index: number) => {
    setMenu(index);
  };

  /**
   * Handling Export Type
   * @param {string} value - Getting a QR code export type value
   */
  const handleQRExport = (value: string) => {
    setQrExport(value);
  };

  /**
   * Handling QR Code Download for single and multi QR codes (PNG & PDF)
   */
  const handleQRDownload = async () => {
    const listToDownload =
      serialNumbersToDownload.length > 0
        ? serialNumbersToDownload
        : [qrValues.deviceSNo || 'SN001'];

    if (qrExport === 'png') {
      for (let initial = 0; initial < listToDownload.length; initial += 1) {
        const element =
          qrReference.current[initial] ||
          (document.querySelector('.qr-code-preview') as HTMLElement | null);

        if (element instanceof HTMLElement) {
          // eslint-disable-next-line no-await-in-loop
          const canvas = await html2canvas(element, { useCORS: true, scale: 2 });
          const data = canvas.toDataURL('image/png');
          const link = document.createElement('a');

          link.href = data;
          const serialNumber = listToDownload[initial] || `SN_${initial + 1}`;
          link.download = `QRCode_${serialNumber}.png`;

          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);

          // eslint-disable-next-line no-await-in-loop
          await new Promise((resolve) => {
            setTimeout(resolve, 150);
          });
        }
      }
    } else if (qrExport === 'pdf') {
      /* eslint-disable-next-line new-cap */
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      let isFirstPage = true;

      for (let initial = 0; initial < listToDownload.length; initial += 1) {
        const element =
          qrReference.current[initial] ||
          (document.querySelector('.qr-code-preview') as HTMLElement | null);

        if (element instanceof HTMLElement) {
          // eslint-disable-next-line no-await-in-loop
          const canvas = await html2canvas(element, { useCORS: true, scale: 2 });
          const imgData = canvas.toDataURL('image/png');

          const imgWidth = 140;
          const imgHeight = (canvas.height * imgWidth) / canvas.width;
          const xPos = (210 - imgWidth) / 2;
          const yPos = 20;

          if (!isFirstPage) {
            pdf.addPage();
          }
          pdf.addImage(imgData, 'PNG', xPos, yPos, imgWidth, imgHeight);
          isFirstPage = false;
        }
      }
      const countName =
        listToDownload.length > 1 ? `_Batch_${listToDownload.length}` : `_${listToDownload[0]}`;
      pdf.save(`QRCode${countName}.pdf`);
    }
  };

  /**
   * Handling QR Method / state changes
   * @param {string} name - The state property name
   * @param {any} value - The state property value
   */
  const handleChangeFunction = (name: string, value: any) => {
    setQrValues((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  /**
   * Handling file remove action
   * @param {string} name - Input file name to remove
   */
  const handleRemoveFile = (name: string) => {
    if (name) {
      setQrValues((previous) => ({
        ...previous,
        [name]: null,
      }));
    }
    if (name === 'xlFile') {
      setSerialNumbersToDownload(qrValues.deviceSNo ? [qrValues.deviceSNo] : []);
    }
  };

  /**
   * Effect to process uploaded charger list files (Excel / CSV / TXT)
   */
  useEffect(() => {
    if (qrValues.xlFile && qrValues.xlFile.length > 0) {
      qrValues.xlFile.forEach((eachFile) => {
        const isCsvOrTxt =
          eachFile.name.endsWith('.csv') ||
          eachFile.name.endsWith('.txt') ||
          eachFile.type.includes('csv') ||
          eachFile.type.includes('plain');

        const reader = new FileReader();

        /**
         * Event handler for FileReader load event
         * @param {ProgressEvent<FileReader>} event - File reader progress event
         */
        const handleFileLoad = (event: ProgressEvent<FileReader>) => {
          if (event.target?.result) {
            const { result } = event.target;

            if (isCsvOrTxt) {
              const textData = result as string;
              const lines = textData.split(/\r?\n/);
              const deviceSNList: string[] = [];

              if (lines.length > 0) {
                const headers = lines[0].split(/[,;\t]/).map((h) => h.trim());
                let targetColIndex = headers.findIndex((h) =>
                  /serial|device|charger|sn|number|id/i.test(h),
                );

                if (targetColIndex === -1 && headers.length > 0) {
                  targetColIndex = 0;
                }

                const startRow = targetColIndex !== -1 && lines.length > 1 ? 1 : 0;
                for (let index = startRow; index < lines.length; index += 1) {
                  if (lines[index].trim()) {
                    const rowValues = lines[index].split(/[,;\t]/);
                    const snValue = rowValues[targetColIndex] || rowValues[0];
                    if (snValue && snValue.trim()) {
                      deviceSNList.push(snValue.trim());
                    }
                  }
                }
              }

              setQrValues((previous) => ({
                ...previous,
                deviceSerialNumbers: deviceSNList,
              }));
              setSerialNumbersToDownload(deviceSNList);
            } else {
              let data: Uint8Array;
              if (result instanceof ArrayBuffer) {
                data = new Uint8Array(result);
              } else {
                const stringResult = result as string;
                const buffer = new ArrayBuffer(stringResult.length);
                const view = new Uint8Array(buffer);
                for (let index = 0; index < stringResult.length; index += 1) {
                  view[index] = stringResult.charCodeAt(index);
                }
                data = new Uint8Array(buffer);
              }

              const workbook = XLSX.read(data, { type: 'array' });
              const sheetName = workbook.SheetNames[0];
              const sheetData = XLSX.utils.sheet_to_json(workbook.Sheets[sheetName]) as Record<
                string,
                any
              >[];

              const deviceSnList: string[] = [];
              sheetData.forEach((eachRow) => {
                const matchedKey = Object.keys(eachRow).find((k) =>
                  /serial|device|charger|sn|number|id/i.test(k),
                );
                const keyToUse = matchedKey || Object.keys(eachRow)[0];
                if (keyToUse && eachRow[keyToUse] !== undefined) {
                  deviceSnList.push(eachRow[keyToUse].toString().trim());
                }
              });

              setQrValues((previous) => ({
                ...previous,
                deviceSerialNumbers: deviceSnList,
              }));
              setSerialNumbersToDownload(deviceSnList);
            }
          }
        };

        reader.onload = handleFileLoad;

        if (isCsvOrTxt) {
          reader.readAsText(eachFile);
        } else {
          reader.readAsArrayBuffer(eachFile);
        }
      });
    }
  }, [qrValues.xlFile]);

  /**
   * Effect to update serial numbers to download when single device serial number changes
   */
  useEffect(() => {
    if (qrValues.qrMethod !== 'MultiQR') {
      setSerialNumbersToDownload([qrValues.deviceSNo || 'SN001']);
    }
  }, [qrValues.deviceSNo, qrValues.qrMethod]);

  /**
   * Set page title on mount
   */
  useEffect(() => {
    dispatch(setPageTitle(t('qrCodeGenerator')));
  }, [dispatch, t]);

  const activeSerialNumber = serialNumbersToDownload[0] || qrValues.deviceSNo || 'SN001';

  return (
    <div className="qr-code-field-wrap">
      <div className="qr-code-field-top-row">
        <div className="qr-code-logo-input">
          <div className="input-wrap">
            <SelectField
              options={[
                { label: t('singleQR'), value: 'singleQR' },
                { label: t('multiQR'), value: 'MultiQR' },
              ]}
              placeholder={t('qrType')}
              selectedValue={qrValues.qrMethod}
              name="qrMethod"
              onChangeValue={handleChangeFunction}
            />
            {qrValues.qrMethod === 'MultiQR' ? (
              <CustomFileInput
                acceptedFileType=".xls,.xlsx,.csv,.txt"
                placeholder={
                  qrValues.xlFile && qrValues.xlFile[0]
                    ? qrValues.xlFile[0].name
                    : t('chooseFile')
                }
                name="xlFile"
                selectedFiles={qrValues.xlFile}
                onChangeValue={handleChangeFunction}
                unselectHandler={handleRemoveFile}
                valueRequired
              />
            ) : (
              <CustomInput
                type="text"
                name="deviceSNo"
                placeholder={t('enterDeviceSNo')}
                value={qrValues.deviceSNo}
                onChangeValue={handleChangeFunction}
              />
            )}

            <CustomFileInput
              acceptedFileType="image/*"
              placeholder={
                qrValues.logo && qrValues.logo[0] ? qrValues.logo[0].name : t('uploadLogo')
              }
              name="logo"
              selectedFiles={qrValues.logo}
              onChangeValue={handleChangeFunction}
              unselectHandler={handleRemoveFile}
              valueRequired
            />
            <SelectField
              options={[{ label: t('templates'), value: 'templates' }]}
              placeholder={t('selectTemplate')}
              selectedValue="templates"
              name="qrTemplates"
              onChangeValue={handleChangeFunction}
            />
          </div>
        </div>
      </div>
      <div className="qr-code-mid-row">
        <div className="qr-code-editor">
          <CustomToggleButton
            labels={[t('qrConfiguration'), t('qrStyle'), t('template')]}
            defaultActiveIndex={0}
            onToggle={handelSelection}
          />
          {styleComponents.map((component) => (
            <div key={component.index}>
              {menu === component.index ? component.component : null}
            </div>
          ))}
        </div>
        <div className="qr-code-wrap">
          <div className="qr-code-preview">
            <div className="qr-code-container">
              {values.map((value) => (
                <div key={value.id}>
                  {value.key === 'qr-code' ? (
                    <div>
                      <QRCode
                        value={getFormattedQrValue(activeSerialNumber)}
                        qrStyle={qrValues.qrstyle}
                        eyeRadius={[
                          {
                            outer: qrValues.outerEye,
                            inner: qrValues.innerEye,
                          },
                          {
                            outer: qrValues.outerEye,
                            inner: qrValues.innerEye,
                          },
                          {
                            outer: qrValues.outerEye,
                            inner: qrValues.innerEye,
                          },
                        ]}
                        fgColor={qrValues.fgColor}
                        bgColor={qrValues.bgColor}
                        eyeColor={[
                          {
                            outer: qrValues.outerColor,
                            inner: qrValues.innerColor,
                          },
                          {
                            outer: qrValues.outerColor,
                            inner: qrValues.innerColor,
                          },
                          {
                            outer: qrValues.outerColor,
                            inner: qrValues.innerColor,
                          },
                        ]}
                        logoImage={logoUrl}
                        logoWidth={qrValues.logoWidth}
                        logoOpacity={qrValues.logoTrans}
                        removeQrCodeBehindLogo
                        ecLevel="H"
                        size={qrValues.qrSize}
                        quietZone={qrValues.qrMargin}
                      />
                    </div>
                  ) : (
                    <p
                      style={{
                        fontFamily: qrValues.font || value.fontFamily,
                        color: qrValues.fontColor,
                        fontWeight: qrValues.fontWeight,
                        fontSize: `${value.fontSize}px`,
                        display: `${value.display === true ? '' : 'none'}`,
                      }}>
                      {value.key === 'SN' ? activeSerialNumber : value.text}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="hidden-qr-export-container">
            {serialNumbersToDownload.map((serial, index) => (
              <div
                key={serial + index.toString()}
                className="qr-code-preview"
                ref={(element) => {
                  qrReference.current[index] = element;
                }}>
                <div className="qr-code-container">
                  {values.map((value) => (
                    <div key={value.id}>
                      {value.key === 'qr-code' ? (
                        <div>
                          <QRCode
                            value={getFormattedQrValue(serial)}
                            qrStyle={qrValues.qrstyle}
                            eyeRadius={[
                              {
                                outer: qrValues.outerEye,
                                inner: qrValues.innerEye,
                              },
                              {
                                outer: qrValues.outerEye,
                                inner: qrValues.innerEye,
                              },
                              {
                                outer: qrValues.outerEye,
                                inner: qrValues.innerEye,
                              },
                            ]}
                            fgColor={qrValues.fgColor}
                            bgColor={qrValues.bgColor}
                            eyeColor={[
                              {
                                outer: qrValues.outerColor,
                                inner: qrValues.innerColor,
                              },
                              {
                                outer: qrValues.outerColor,
                                inner: qrValues.innerColor,
                              },
                              {
                                outer: qrValues.outerColor,
                                inner: qrValues.innerColor,
                              },
                            ]}
                            logoImage={logoUrl}
                            logoWidth={qrValues.logoWidth}
                            logoOpacity={qrValues.logoTrans}
                            removeQrCodeBehindLogo
                            ecLevel="H"
                            size={qrValues.qrSize}
                            quietZone={qrValues.qrMargin}
                          />
                        </div>
                      ) : (
                        <p
                          style={{
                            fontFamily: qrValues.font || value.fontFamily,
                            color: qrValues.fontColor,
                            fontWeight: qrValues.fontWeight,
                            fontSize: `${value.fontSize}px`,
                            display: `${value.display === true ? '' : 'none'}`,
                          }}>
                          {value.key === 'SN' ? serial : value.text}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="qr-export-container">
            <div className="qr-export-type">
              <CustomRadioButton
                selected={qrExport === 'png'}
                value="png"
                label={t('png')}
                name="png"
                onChangeFunc={handleQRExport}
              />
              <CustomRadioButton
                selected={qrExport === 'pdf'}
                value="pdf"
                label={t('pdf')}
                name="pdf"
                onChangeFunc={handleQRExport}
              />
            </div>
            <div className="qr-download">
              <div className="save">
                <MyButton
                  label={t('saveTemplate')}
                  buttonType="submit"
                  onClickFunc={() => {}}
                  disableButton={!permissionCheck('cpoQrCodeGenerationSaveTemplate')}
                />
              </div>
              <div className="download">
                <MyButton
                  label={t('download')}
                  buttonType="submit"
                  onClickFunc={handleQRDownload}
                  disableButton={
                    !permissionCheck('partnerQrCodeGenerate') &&
                    !permissionCheck('cpoQrCodeGenerationDownload')
                  }
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default QrCodeInputField;
